import React, { useState, useEffect, useCallback } from 'react'
    import { motion, AnimatePresence } from 'framer-motion'
    import * as FiIcons from 'react-icons/fi'
    import SafeIcon from './common/SafeIcon'
    import { useSimpleAuth } from './hooks/useSimpleAuth'
    import { usePeople, useRelationships } from './hooks/useSupabase'
    import LoginPage from './components/LoginPage'
    import UserProfile from './components/UserProfile'
    import PersonCard from './components/PersonCard'
    import PersonForm from './components/PersonForm'
    import RelationshipModal from './components/RelationshipModal'
    import SearchBar from './components/SearchBar'
    import ExitWarning from './components/ExitWarning'
    import './App.css'

    const {
      FiUsers,
      FiPlus,
      FiRefreshCw,
      FiFilter,
      FiMapPin,
      FiX,
    } = FiIcons

    function App() {
      const { user, loading: authLoading, signOut } = useSimpleAuth()
      const {
        people,
        loading,
        error,
        fetchPeople,
        searchPeople,
        addPerson,
        updatePerson,
        deletePerson,
      } = usePeople()
      const {
        fetchRelationships,
        cleanupDuplicateRelationships,
        addRelationship,
        updateRelationship,
        deleteRelationship,
        syncAllFamilyRelationships,
      } = useRelationships()

      const [showPersonForm, setShowPersonForm] = useState(false)
      const [editingPerson, setEditingPerson] = useState(null)
      const [selectedPerson, setSelectedPerson] = useState(null)
      const [searchResults, setSearchResults] = useState([])
      const [allRelationships, setAllRelationships] = useState([])
      const [showExitWarning, setShowExitWarning] = useState(false)
      const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0)
      const [searchLoading, setSearchLoading] = useState(false)
      const [proximityFilter, setProximityFilter] = useState('all')
      const [showFilters, setShowFilters] = useState(false)
      const [authError, setAuthError] = useState(null)
      const [searchQuery, setSearchQuery] = useState('')
      const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery)
      const [formSubmitError, setFormSubmitError] = useState(null)
      const [historyState, setHistoryState] = useState({ initialized: false })
      const [systemMaintenanceInProgress, setSystemMaintenanceInProgress] =
        useState(false)
      const [personToDelete, setPersonToDelete] = useState(null) // Track person to delete
      const [personPromise, setPersonPromise] = useState(null) // For adding person from relationship modal
      const [personToFocus, setPersonToFocus] = useState(null)
      const [newPersonInitialName, setNewPersonInitialName] = useState('')

      // State for nested PersonForm
      const [showNestedPersonForm, setShowNestedPersonForm] = useState(false);
      const [nestedPersonInitialName, setNestedPersonInitialName] = useState('');
      const [nestedPersonPromise, setNestedPersonPromise] = useState(null);

      const triggerDataRefresh = () => {
        setDataRefreshTrigger((prev) => prev + 1)
      }

      const safelyAddHistoryEntry = useCallback(() => {
        if (!historyState.initialized) {
          window.history.pushState({ page: 'main' }, '', window.location.pathname)
          setHistoryState({ initialized: true })
        }
      }, [historyState])

      const fetchAllRelationships = useCallback(async () => {
        try {
          const allRels = await fetchRelationships()
          setAllRelationships(allRels || [])
        } catch (error) {
          console.error('Error fetching all relationships:', error)
        }
      }, [fetchRelationships])

      useEffect(() => {
        if (user) {
          fetchPeople()
          fetchAllRelationships()
        }
      }, [user, dataRefreshTrigger, fetchPeople, fetchAllRelationships])

      useEffect(() => {
        safelyAddHistoryEntry()
      }, [safelyAddHistoryEntry])

      // Debounce search query
      useEffect(() => {
        const handler = setTimeout(() => {
          setDebouncedSearchQuery(searchQuery)
        }, 300)

        return () => {
          clearTimeout(handler)
        }
      }, [searchQuery])

      // Perform search when debounced query changes
      useEffect(() => {
        const performSearch = async () => {
          if (debouncedSearchQuery.trim()) {
            setSearchLoading(true)
            try {
              const results = await searchPeople(debouncedSearchQuery.trim())
              setSearchResults(results)
            } catch (error) {
              console.error('Error searching:', error)
              setSearchResults([])
            } finally {
              setSearchLoading(false)
            }
          } else {
            setSearchResults([])
          }
        }
        performSearch()
      }, [debouncedSearchQuery, searchPeople])

      useEffect(() => {
        const handleBackButton = (e) => {
          if (showNestedPersonForm) {
            e.preventDefault();
            handleNestedPersonFormCancel();
            return;
          }
          if (showPersonForm) {
            e.preventDefault()
            setShowPersonForm(false)
            setEditingPerson(null)
            return
          }
          if (selectedPerson) {
            e.preventDefault()
            setSelectedPerson(null)
            return
          }
          if (personToDelete) {
            e.preventDefault()
            setPersonToDelete(null)
            return
          }
          if (user && !showExitWarning) {
            e.preventDefault()
            setShowExitWarning(true)
          }
        }
        window.addEventListener('popstate', handleBackButton)
        return () => {
          window.removeEventListener('popstate', handleBackButton)
        }
      }, [user, showPersonForm, selectedPerson, showExitWarning, personToDelete, showNestedPersonForm])

      const getPersonRelationships = (personId) => {
        return allRelationships.filter(
          (rel) => rel.person_a_id === personId || rel.person_b_id === personId
        )
      }

      const handleAuthSuccess = (user) => {
        console.log('Authentication successful:', user)
        setAuthError(null)
      }

      const handleAuthError = (error) => {
        console.error('Authentication error:', error)
        setAuthError(error.message)
      }

      const processNewRelationships = async (savedPerson, newRelationships, isEditing) => {
        if (!newRelationships || newRelationships.length === 0 || !savedPerson) {
          return;
        }
        const updatedPeopleList = isEditing
          ? people.map((p) => (p.id === savedPerson.id ? savedPerson : p))
          : [...people, savedPerson];
        const currentRelationships = [...allRelationships];
    
        for (const rel of newRelationships) {
          if (rel.relatedPersonId && rel.relationshipType) {
            try {
              const result = await addRelationship(
                savedPerson.id,
                rel.relatedPersonId,
                rel.relationshipType,
                updatedPeopleList,
                currentRelationships
              );
              if (result.mainRelationship) {
                currentRelationships.push(result.mainRelationship);
              }
              if (result.smartRelationships && result.smartRelationships.length > 0) {
                currentRelationships.push(...result.smartRelationships);
              }
            } catch (error) {
              console.error('Error adding relationship:', error);
            }
          }
        }
        await fetchAllRelationships();
      };
      
      const handlePersonSubmit = async (personData) => {
        try {
          setFormSubmitError(null)
          let savedPerson
          const { newRelationships, ...personDataWithoutRelationships } = personData
          if (editingPerson) {
            savedPerson = await updatePerson(
              editingPerson.id,
              personDataWithoutRelationships
            )
          } else {
            savedPerson = await addPerson(personDataWithoutRelationships)
          }

          await processNewRelationships(savedPerson, newRelationships, !!editingPerson);

          setShowPersonForm(false)
          setEditingPerson(null)
          setNewPersonInitialName('')
          triggerDataRefresh()

          if (personPromise) {
            personPromise.resolve(savedPerson)
            setSelectedPerson(personPromise.originalPerson)
            setPersonPromise(null)
          }
          return savedPerson
        } catch (error) {
          console.error('Error saving person:', error)
          setFormSubmitError(error.message || 'Failed to save person')
          if (personPromise) {
            personPromise.reject(error)
            setSelectedPerson(personPromise.originalPerson)
            setPersonPromise(null)
          }
          setNewPersonInitialName('')
          throw error
        }
      }

      const handleNestedPersonSubmit = async (personData) => {
        try {
          setFormSubmitError(null);
          const { newRelationships, ...personDataWithoutRelationships } = personData;
          
          // Nested form always adds a new person
          const savedPerson = await addPerson(personDataWithoutRelationships);
      
          await processNewRelationships(savedPerson, newRelationships, false);
      
          setShowNestedPersonForm(false);
          setNestedPersonInitialName('');
          triggerDataRefresh();
      
          if (nestedPersonPromise) {
            nestedPersonPromise.resolve(savedPerson);
            setNestedPersonPromise(null);
          }
          return savedPerson;
        } catch (error) {
          console.error('Error saving nested person:', error);
          setFormSubmitError(error.message || 'Failed to save person');
          if (nestedPersonPromise) {
            nestedPersonPromise.reject(error);
            setNestedPersonPromise(null);
          }
          throw error;
        }
      };

      const handlePersonDelete = async (personId) => {
        const personToDelete =
          people.find((p) => p.id === personId) ||
          searchResults.find((p) => p.id === personId)
        if (personToDelete) {
          setPersonToDelete({ id: personId, name: personToDelete.name })
        }
      }

      const confirmDeletePerson = async () => {
        if (personToDelete) {
          try {
            await deletePerson(personToDelete.id)
            if (isSearching) {
              setSearchResults((prevResults) =>
                prevResults.filter((person) => person.id !== personToDelete.id)
              )
            }
            setPersonToDelete(null)
            triggerDataRefresh()
          } catch (error) {
            console.error('Error deleting person:', error)
          }
        }
      }

      const cancelDeletePerson = () => {
        setPersonToDelete(null)
      }

      const handleAddPersonFromRelationship = (initialName = '') => {
        return new Promise((resolve, reject) => {
          setPersonPromise({ resolve, reject, originalPerson: selectedPerson })
          setSelectedPerson(null) // Hide RelationshipModal
          setEditingPerson(null) // Ensure it's 'add' mode
          setFormSubmitError(null)
          setNewPersonInitialName(initialName || '')
          setShowPersonForm(true) // Show PersonForm
        })
      }

      const handleNestedAddPerson = (initialName = '') => {
        return new Promise((resolve, reject) => {
          setNestedPersonPromise({ resolve, reject });
          setNestedPersonInitialName(initialName || '');
          setShowNestedPersonForm(true);
        });
      };

      const handleQuickAddPerson = async (name) => {
        const trimmedName = (name || '').trim()
        if (!trimmedName) return null
        try {
          const newPerson = await addPerson({
            name: trimmedName,
            sex: 'other',
            nicknames: '',
            notes: '',
            proximity: 'Medium',
            is_family: true,
          })
          return newPerson
        } catch (error) {
          console.error('Error quickly adding person:', error)
          throw error
        }
      }

      const handlePersonFormCancel = () => {
        setShowPersonForm(false)
        setEditingPerson(null)
        setFormSubmitError(null)
        setNewPersonInitialName('')
        if (personPromise) {
          personPromise.resolve(null) // Cancelled
          setSelectedPerson(personPromise.originalPerson) // Restore relationship modal
          setPersonPromise(null)
        }
      }

      const handleNestedPersonFormCancel = () => {
        setShowNestedPersonForm(false);
        setNestedPersonInitialName('');
        if (nestedPersonPromise) {
          nestedPersonPromise.resolve(null); // Cancelled
          setNestedPersonPromise(null);
        }
      };

      const handlePersonUpdateFromRelationship = async (personId, personData) => {
        try {
          await updatePerson(personId, personData)
          triggerDataRefresh()
        } catch (error) {
          console.error(
            'Error updating person from relationship modal:',
            error
          )
          throw error
        }
      }
      
      const handleAddRelationships = async (personId, relationshipData) => {
        let totalSmartRelationships = 0
        const allExplanations = []
        const currentRelationships = [...allRelationships]
        for (const { relatedPersonId, relationshipType } of relationshipData) {
          const result = await addRelationship(
            personId,
            relatedPersonId,
            relationshipType,
            people,
            currentRelationships
          )
          if (result.mainRelationship) {
            currentRelationships.push(result.mainRelationship)
          }
          if (result.smartRelationships && result.smartRelationships.length > 0) {
            currentRelationships.push(...result.smartRelationships)
            totalSmartRelationships += result.smartRelationshipCount
            allExplanations.push(...result.explanations)
          }
        }
        await fetchAllRelationships()
        return { count: totalSmartRelationships, explanations: allExplanations }
      }

      const handleUpdateRelationship = async (relationshipId, updateData, currentPersonId) => {
        await updateRelationship(relationshipId, updateData, currentPersonId)
        await fetchAllRelationships()
      }

      const handleDeleteRelationship = async (relationshipId) => {
        await deleteRelationship(relationshipId)
        await fetchAllRelationships()
      }

      const handleCleanupDuplicates = async () => {
        await cleanupDuplicateRelationships()
        await fetchAllRelationships()
      }

      const handleExitConfirm = () => {
        setShowExitWarning(false)
        window.close()
      }

      const handleExitCancel = () => {
        setShowExitWarning(false)
      }

      const handleRelationshipModalClose = () => {
        setSelectedPerson(null)
        triggerDataRefresh()
      }

      const handleProximityFilterChange = (value) => {
        setProximityFilter(value)
      }

      const runSystemMaintenance = async () => {
        try {
          setSystemMaintenanceInProgress(true)
          const result = await cleanupDuplicateRelationships()
          await fetchAllRelationships()
          alert(
            `System maintenance completed. Fixed ${result.cleaned} duplicate relationships.`
          )
        } catch (error) {
          console.error('Maintenance error:', error)
          alert('System maintenance failed: ' + error.message)
        } finally {
          setSystemMaintenanceInProgress(false)
        }
      }

      const handleFamilySync = async () => {
        try {
          setSystemMaintenanceInProgress(true)
          const result = await syncAllFamilyRelationships()
          alert(
            `Family sync complete. Added ${result.addedCount} new relationships.`
          )
          triggerDataRefresh()
        } catch (error) {
          console.error('Family sync failed:', error)
          alert('An error occurred during family sync: ' + error.message)
        } finally {
          setSystemMaintenanceInProgress(false)
        }
      }

      const isSearching = debouncedSearchQuery.trim().length > 0

      const getFilteredPeople = () => {
        let filtered = isSearching ? searchResults : people
        if (proximityFilter !== 'all') {
          const filterValue =
            proximityFilter.charAt(0).toUpperCase() +
            proximityFilter.slice(1).toLowerCase()
          filtered = filtered.filter((p) => {
            const personProximity = p.proximity || 'Medium'
            return personProximity === filterValue
          })
        }
        return filtered.sort((a, b) => {
          const proximityOrder = { Close: 1, Medium: 2, Far: 3 }
          const aOrder = proximityOrder[a.proximity || 'Medium'] || 2
          const bOrder = proximityOrder[b.proximity || 'Medium'] || 2
          return aOrder - bOrder
        })
      }

      const displayedPeople = getFilteredPeople()

      const handleNavigateToPerson = (personId) => {
        setSearchQuery('')
        setPersonToFocus(personId)
      }

      useEffect(() => {
        if (personToFocus && displayedPeople.some((p) => p.id === personToFocus)) {
          const timer = setTimeout(() => {
            const element = document.getElementById(`person-${personToFocus}`)
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' })
              element.classList.add(
                'ring-4',
                'ring-blue-500',
                'transition-all',
                'duration-500',
                'ease-in-out'
              )
              setTimeout(() => {
                element.classList.remove('ring-4', 'ring-blue-500')
              }, 2500)
            }
            setPersonToFocus(null)
          }, 150)
          return () => clearTimeout(timer)
        }
      }, [personToFocus, displayedPeople])

      const getProximityColor = (proximity) => {
        switch ((proximity || '').toLowerCase()) {
          case 'close':
            return 'bg-green-100 text-green-700 border-green-200'
          case 'medium':
            return 'bg-yellow-100 text-yellow-700 border-yellow-200'
          case 'far':
            return 'bg-blue-100 text-blue-700 border-blue-200'
          default:
            return 'bg-gray-100 text-gray-700 border-gray-200'
        }
      }

      if (authLoading) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        )
      }

      if (!user) {
        return (
          <LoginPage
            onAuthSuccess={handleAuthSuccess}
            onAuthError={handleAuthError}
            authError={authError}
          />
        )
      }

      return (
        <div className="min-h-screen bg-gray-50">
          <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon
                    icon={FiUsers}
                    className="text-2xl sm:text-3xl text-blue-600"
                  />
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                    {' '}
                    Relationship Tracker{' '}
                  </h1>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`px-3 py-2 rounded-lg flex items-center space-x-1 transition-colors ${
                      proximityFilter !== 'all'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <SafeIcon icon={FiFilter} />
                    <span className="hidden sm:inline">Filter</span>
                    {proximityFilter !== 'all' && (
                      <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded-full">
                        {' '}
                        1{' '}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      triggerDataRefresh()
                    }}
                    disabled={loading || systemMaintenanceInProgress}
                    className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                  >
                    <SafeIcon
                      icon={FiRefreshCw}
                      className={
                        loading || systemMaintenanceInProgress ? 'animate-spin' : ''
                      }
                    />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowPersonForm(true)
                      setEditingPerson(null)
                      setFormSubmitError(null)
                      setNewPersonInitialName('')
                    }}
                    className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    <SafeIcon icon={FiPlus} />
                    <span className="hidden sm:inline">Add Person</span>
                  </button>
                  <UserProfile
                    user={user}
                    onSignOut={signOut}
                    onFamilySync={handleFamilySync}
                  />
                </div>
              </div>
              <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm py-4 -mx-2 sm:-mx-4 px-2 sm:px-4 mb-4 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="max-w-md w-full">
                  <SearchBar query={searchQuery} onQueryChange={setSearchQuery} />
                </div>
                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-2 sm:mt-0"
                    >
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiMapPin} className="text-gray-500" />
                        <span className="text-sm text-gray-700">Proximity:</span>
                        <div className="flex space-x-1">
                          {['all', 'close', 'medium', 'far'].map((option) => (
                            <button
                              key={option}
                              onClick={() => handleProximityFilterChange(option)}
                              className={`px-2 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                                proximityFilter === option
                                  ? option === 'all'
                                    ? 'bg-gray-700 text-white'
                                    : getProximityColor(option)
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                        {proximityFilter !== 'all' && (
                          <button
                            onClick={() => handleProximityFilterChange('all')}
                            className="text-gray-500 hover:text-gray-700"
                            title="Clear filter"
                          >
                            <SafeIcon icon={FiX} size={16} />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">Error: {error}</p>
              </div>
            )}
            {formSubmitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-800">Form Error: {formSubmitError}</p>
              </div>
            )}
            {isSearching && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-blue-800">
                    {' '}
                    {searchLoading
                      ? 'Searching...'
                      : `Found ${searchResults.length} result${
                          searchResults.length !== 1 ? 's' : ''
                        }`}{' '}
                  </p>
                  {searchLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                </div>
              </div>
            )}
            {proximityFilter !== 'all' && !isSearching && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiFilter} className="text-blue-600" />
                    <p className="text-blue-800">
                      {' '}
                      Showing {displayedPeople.length} {proximityFilter} contact
                      {displayedPeople.length !== 1 ? 's' : ''}{' '}
                    </p>
                  </div>
                  <button
                    onClick={() => handleProximityFilterChange('all')}
                    className="text-blue-600 hover:text-blue-800"
                    title="Clear filter"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
              </div>
            )}

            {process.env.NODE_ENV !== 'production' && (
              <div className="mb-4">
                <button
                  onClick={runSystemMaintenance}
                  disabled={systemMaintenanceInProgress}
                  className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm flex items-center"
                >
                  {systemMaintenanceInProgress ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Running system maintenance...
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiRefreshCw} className="mr-2" />
                      Run System Maintenance (Fix Duplicates)
                    </>
                  )}
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <AnimatePresence>
                {displayedPeople.map((person) => (
                  <PersonCard
                    key={`${person.id}-${person.updated_at}`}
                    person={person}
                    relationships={getPersonRelationships(person.id)}
                    people={people}
                    onEdit={(p) => {
                      setEditingPerson(p)
                      setShowPersonForm(true)
                      setFormSubmitError(null)
                      setNewPersonInitialName('')
                    }}
                    onDelete={handlePersonDelete}
                    onViewRelationships={(p) => {
                      setSelectedPerson(p)
                    }}
                    onNavigateToPerson={handleNavigateToPerson}
                  />
                ))}
              </AnimatePresence>
            </div>

            {(loading) && !isSearching && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">
                  {' '}
                  Loading people...{' '}
                </p>
              </div>
            )}
            {!loading && !searchLoading && displayedPeople.length === 0 && (
              <div className="text-center py-12">
                <SafeIcon icon={FiUsers} className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">
                  {' '}
                  {isSearching
                    ? 'No people found matching your search.'
                    : proximityFilter !== 'all'
                    ? `No ${proximityFilter} contacts found.`
                    : 'No people recorded yet.'}{' '}
                </p>
                {!isSearching && proximityFilter === 'all' && (
                  <p className="text-gray-500 mt-2">
                    {' '}
                    Click "Add Person" to get started tracking relationships.{' '}
                  </p>
                )}
                {proximityFilter !== 'all' && (
                  <button
                    onClick={() => handleProximityFilterChange('all')}
                    className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                  >
                    {' '}
                    Show all contacts{' '}
                  </button>
                )}
              </div>
            )}
          </div>

          <AnimatePresence>
            {showPersonForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 fixed-modal"
              >
                <PersonForm
                  person={editingPerson}
                  people={people}
                  existingRelationships={
                    editingPerson ? getPersonRelationships(editingPerson.id) : []
                  }
                  allRelationships={allRelationships}
                  onSubmit={handlePersonSubmit}
                  onCancel={handlePersonFormCancel}
                  onAddNewPerson={handleQuickAddPerson}
                  onOpenPersonForm={handleNestedAddPerson}
                  initialName={editingPerson ? '' : newPersonInitialName}
                />
              </motion.div>
            )}

            {showNestedPersonForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-2 sm:p-4 fixed-modal"
              >
                <PersonForm
                  person={null}
                  people={people}
                  existingRelationships={[]}
                  allRelationships={allRelationships}
                  onSubmit={handleNestedPersonSubmit}
                  onCancel={handleNestedPersonFormCancel}
                  onAddNewPerson={handleQuickAddPerson}
                  onOpenPersonForm={null}
                  initialName={nestedPersonInitialName}
                />
              </motion.div>
            )}

            {selectedPerson && (
              <RelationshipModal
                person={selectedPerson}
                people={people}
                allRelationships={allRelationships}
                onClose={handleRelationshipModalClose}
                onAddPerson={handleAddPersonFromRelationship}
                onPersonUpdate={handlePersonUpdateFromRelationship}
                onAddRelationships={handleAddRelationships}
                onUpdateRelationship={handleUpdateRelationship}
                onDeleteRelationship={handleDeleteRelationship}
                onCleanupDuplicates={handleCleanupDuplicates}
              />
            )}

            {personToDelete && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-auto"
                >
                  <div className="text-center mb-6">
                    <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <SafeIcon icon={FiUsers} className="text-2xl text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">
                      {' '}
                      Delete Person{' '}
                    </h2>
                    <p className="text-gray-600 mt-2">
                      {' '}
                      Are you sure you want to delete{' '}
                      <strong>{personToDelete.name}</strong>? This will also
                      remove all their relationships.{' '}
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={cancelDeletePerson}
                      className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiX} />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={confirmDeletePerson}
                      className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiUsers} />
                      <span>Delete</span>
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {showExitWarning && (
              <ExitWarning
                onConfirm={handleExitConfirm}
                onCancel={handleExitCancel}
              />
            )}
          </AnimatePresence>
        </div>
      )
    }

    export default App